/**
 * Deferred, cancellable actions for the TA Action Board.
 *
 * Anything irreversible — archiving in Lever, sending an email to a candidate —
 * waits behind a countdown with a Stop button. Nothing at all runs during the
 * wait, so cancelling leaves Lever, the mailbox and the log untouched.
 *
 * A QUEUE, not a single slot. It used to hold one pending action, so confirming
 * a second within the countdown replaced the first: that action then never ran
 * AND never cancelled, silently, while its card already showed as handled. The
 * whole point of the delay is that the recruiter carries on working, so several
 * countdowns have to be able to run at once.
 *
 * Kept out of BoardChrome.tsx so that file exports only components (react-refresh).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/** Seconds a confirmed action waits before anything irreversible happens. */
export const UNDO_DELAY_SECONDS = 10;

export interface PendingAction {
  /** Stable id — the candidate/opportunity it concerns, so a re-confirm replaces it. */
  id: string;
  /** What the banner says is about to happen, e.g. "Archiving Max Mustermann". */
  label: string;
  secondsLeft: number;
  /** Fires the real request once the countdown ends. */
  run: () => void;
  /** Puts the board back the way it was; nothing left the building. */
  undo: () => void;
  /**
   * The opportunity ids this action has ALREADY marked handled optimistically,
   * before it has run.
   *
   * Every confirm marks its cards handled immediately — the card must stop
   * looking actionable the moment the recruiter commits — and the board writes
   * `handled` into its localStorage cache on the very next render. That pairing
   * silently loses work: close the tab, reload, or navigate away inside the ten
   * seconds and the action never runs, while the "✓ Invite sent" marker has
   * already been persisted. The card comes back greyed out for ever, with no
   * email, no log entry and no error anywhere (reported Sep 2026 — an invite
   * that was never sent and could never be retried).
   *
   * Naming the ids here is what lets the cache write skip them: a marker for
   * something that has not happened yet must not outlive the tab it was made in.
   */
  affects?: string[];
}

export function usePendingActions() {
  const [pending, setPending] = useState<PendingAction[]>([]);
  /**
   * What is queued and has NOT run, kept in step SYNCHRONOUSLY at every mutation.
   *
   * This is what the unmount cleanup fires, so it cannot be a passive mirror of
   * `pending`: React batches, and two orderings then go wrong. An action
   * deferred in the same batch as the unmount would be missed — the silent drop
   * this whole hook exists to prevent. Worse, an action that has just RUN on a
   * countdown tick would still be in a stale copy, and the cleanup would send it
   * a second time. A duplicate interview invitation is the worst thing this file
   * can produce, so the ref is written where the queue changes, never after.
   */
  const unrun = useRef<PendingAction[]>([]);

  useEffect(() => {
    if (pending.length === 0) return;
    const timer = setTimeout(() => {
      // Collect what is due INSIDE the functional update, from `prev`, then fire
      // outside it. Two separate hazards:
      //  - setting a value derived from the captured `pending` would discard an
      //    action deferred in the same millisecond as this tick — the very
      //    silent-drop this queue exists to eliminate, reintroduced as a race;
      //  - running side effects inside the updater can double-execute, because
      //    React replays updaters (StrictMode, rebased renders), and sending an
      //    email twice is the worst failure this code has.
      const due = pending.filter((p) => p.secondsLeft <= 1);
      const dueIds = new Set(due.map((p) => p.id));
      const seen = new Set(pending.map((p) => p.id));
      // BEFORE run(), so an unmount racing this tick cannot fire them again.
      unrun.current = unrun.current.filter((p) => !dueIds.has(p.id));
      // Functional update, but only touching the entries this render actually
      // saw: anything deferred between that render and this tick is left alone
      // rather than overwritten away.
      setPending((prev) =>
        prev
          .filter((p) => !dueIds.has(p.id))
          .map((p) => (seen.has(p.id) ? { ...p, secondsLeft: p.secondsLeft - 1 } : p))
      );
      for (const action of due) action.run();
    }, 1000);
    return () => clearTimeout(timer);
  }, [pending]);

  /** Queue an action behind the countdown. A same-id entry is replaced (and its undo run). */
  const defer = useCallback(
    (action: Omit<PendingAction, 'secondsLeft'>) => {
      // Read the superseded entry from CURRENT state, not from inside the
      // updater: an updater runs asynchronously, so anything read there is not
      // available to the caller yet. Side effects then run outside it, because
      // React replays updaters and undo must not fire twice.
      const superseded = pending.find((p) => p.id === action.id);
      const queued = { ...action, secondsLeft: UNDO_DELAY_SECONDS };
      unrun.current = [...unrun.current.filter((p) => p.id !== action.id), queued];
      setPending((prev) => [...prev.filter((p) => p.id !== action.id), queued]);
      superseded?.undo();
    },
    [pending]
  );

  /** Stop one pending action and run its undo. */
  const cancel = useCallback(
    (id: string) => {
      const target = pending.find((p) => p.id === id);
      unrun.current = unrun.current.filter((p) => p.id !== id);
      setPending((prev) => prev.filter((p) => p.id !== id));
      target?.undo();
    },
    [pending]
  );

  /**
   * Everything currently marked handled by an action that has NOT run yet.
   *
   * The board excludes these from its persisted cache — see `affects`.
   */
  const pendingIds = useMemo(() => {
    const ids = new Set<string>();
    for (const p of pending) for (const id of p.affects || []) ids.add(id);
    return ids;
  }, [pending]);

  /**
   * Leaving mid-countdown loses the action, so say so.
   *
   * The queue lives in memory: a reload or a closed tab drops every pending
   * send without running or undoing it. The browser's own confirm dialog is
   * the only thing that can intervene at that point.
   */
  useEffect(() => {
    if (pending.length === 0) return undefined;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [pending.length]);

  /**
   * Leaving the board inside the countdown SENDS, rather than silently dropping it.
   *
   * Switching board tabs is safe — those are conditional renders inside one
   * mounted view — but navigating to another People Space page unmounts that
   * view, and with it this queue. Every action in flight then neither ran nor
   * undid: no email, no log entry, no error, and until PR #322 a card left
   * greyed out as "sent" for ever (reported Sep 2026).
   *
   * Running them is the honest resolution of that. The recruiter already
   * confirmed; the countdown is an undo WINDOW, not a second confirmation, and
   * navigating away closes the window rather than revoking the decision. The
   * alternative — dropping the work — makes a button that said "sent" mean
   * nothing, which is the worse failure of the two.
   *
   * A ref, because this must fire on unmount with whatever was queued at that
   * moment: an effect with `pending` in its deps would run its cleanup on every
   * tick of the countdown and send immediately.
   */
  useEffect(
    () => () => {
      for (const action of unrun.current) action.run();
      unrun.current = [];
    },
    []
  );

  return { pending, pendingIds, defer, cancel };
}
